import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  BULK_ASSIGN_COMPLIANCE_QUESTIONS_URL,
  BULK_UNASSIGN_COMPLIANCE_QUESTIONS_URL,
  GET_ASSIGNED_QUESTIONS_FOR_RULE_URL,
  GET_COMPLIANCE_RULES_URL,
  GET_FORMS_URL,
  GET_FORM_STRUCTURE_URL,
  LIST_FORM_TRANSITION_RULES_URL,
  get_request_config
} from '@/utils/system_constants/constant';
import {
  DOCUMENT_QUESTION_TYPES,
  build_document_record,
  extract_response_items,
  extract_rules_response_items,
  normalize_form,
  normalize_rule,
  normalize_transition_rule,
  parse_response_data
} from './document_helpers';
import document_ui_methods from './document_ui_methods';

export default {
  name: 'ManageDocuments',
  mixins: [alert_mixin],
  data: () => ({
    search_query: '',
    current_page: 1,
    page_size: 4,
    loading: false,
    assigning_rule: false,
    updating_document_rule_id: '',
    loading_rule_documents: false,
    loading_rules: false,
    loading_transition_rules: false,
    loading_forms: false,
    loading_documents: false,
    forms: [],
    rules: [],
    transition_rules: [],
    assigned_rule_documents: [],
    all_rule_documents: [],
    visible_rule_document_ids: [],
    selected_rule_id: '',
    selected_transition_rule_id: '',
    selected_form_id: '',
    selected_document_ids: [],
    selected_transition_rule_document_ids: [],
    documents: [],
    selected_document: null
  }),
  computed: {
    selected_form() {
      return this.selectable_forms.find((form) => String(form.id) === String(this.selected_form_id)) || null;
    },
    selected_rule() {
      return this.rules.find((rule) => String(rule.id) === String(this.selected_rule_id)) || null;
    },
    selected_transition_rule() {
      return this.transition_rules.find((rule) => String(rule.id) === String(this.selected_transition_rule_id)) || null;
    },
    compliance_rule_placeholder() {
      if (!this.selected_form_id) {
        return 'Select form first';
      }

      if (this.loading_rules) {
        return 'Loading compliance rules...';
      }

      return this.rules.length
        ? 'Select Compliance Rule'
        : 'No compliance rules for this form';
    },
    transition_rule_placeholder() {
      if (!this.selected_form_id) {
        return 'Select form first';
      }

      if (this.loading_transition_rules) {
        return 'Loading transitions...';
      }

      return this.transition_rules.length
        ? 'Select Transition Rule'
        : 'No transition rules for this form';
    },
    selectable_forms() {
      return this.forms;
    },
    selected_document_count() {
      return this.selected_document_ids.length;
    },
    assigned_rule_document_count() {
      return this.assigned_rule_documents.length;
    },
    assigned_rule_section_question_ids() {
      return new Set(
        this.assigned_rule_documents
          .map((document) => Number(document.section_question_id))
          .filter((id) => Number.isInteger(id) && id > 0)
      );
    },
    all_rule_section_question_ids() {
      return new Set(
        this.all_rule_documents
          .map((document) => Number(document.section_question_id))
          .filter((id) => Number.isInteger(id) && id > 0)
      );
    },
    filtered_documents() {
      const query = String(this.search_query || '').toLowerCase().trim();
      const documents = this.documents;

      if (!query) return documents;

      return documents.filter((document) => {
        const haystack = `${document.name} ${document.description} ${document.type} ${document.section_name} ${document.form_name}`.toLowerCase();
        return haystack.includes(query);
      });
    },
    total_pages() {
      const pages = Math.ceil(this.filtered_documents.length / this.page_size);
      return Math.max(1, pages);
    },
    paginated_documents() {
      const start = (this.current_page - 1) * this.page_size;
      const end = start + this.page_size;
      return this.filtered_documents.slice(start, end);
    },
    pagination_label() {
      const total = this.filtered_documents.length;
      if (!total) return '0 of 0 results';

      const start = (this.current_page - 1) * this.page_size + 1;
      const end = Math.min(this.current_page * this.page_size, total);
      return `${start} to ${end} of ${total} results`;
    }
  },
  watch: {
    search_query() {
      this.current_page = 1;
    },
    selected_form_id() {
      this.current_page = 1;
      this.search_query = '';
      this.selected_document = null;
      this.selected_document_ids = [];
      this.selected_rule_id = '';
      this.selected_transition_rule_id = '';
      this.rules = [];
      this.assigned_rule_documents = [];
      this.all_rule_documents = [];
      this.selected_transition_rule_document_ids = [];
      this.transition_rules = [];
      this.visible_rule_document_ids = [];
      this.fetch_documents_for_selected_form();
    },
    selected_rule_id() {
      this.current_page = 1;
      this.selected_document = null;
      this.visible_rule_document_ids = [];
      this.fetch_documents_for_selected_rule();
    },
    selected_transition_rule_id() {
      this.selected_transition_rule_document_ids = this.get_transition_rule_document_ids(this.selected_transition_rule);
    }
  },
  mounted() {
    this.fetch_forms();
  },
  methods: {
    ...document_ui_methods,
    parse_response_data,
    extract_response_items,
    extract_rules_response_items,
    normalize_form,
    normalize_rule,
    normalize_transition_rule,
    get_question_type(question) {
      const question_type = question?.question_type?.question_type
        || question?.question_type?.type
        || question?.question_type
        || '';

      return String(question_type).trim();
    },
    is_document_question(question) {
      const normalized_type = this.get_question_type(question).toLowerCase();
      return DOCUMENT_QUESTION_TYPES.includes(normalized_type);
    },
    build_document_record(question, section, sub_section) {
      const form_name = this.selected_form?.form_name || '';
      return build_document_record(
        question,
        section,
        sub_section,
        this.selected_form_id,
        form_name,
        this.get_question_type(question)
      );
    },
    extract_document_questions(form_structure) {
      return (Array.isArray(form_structure) ? form_structure : []).flatMap((section) =>
        (section?.sub_sections || []).flatMap((sub_section) =>
          (sub_section?.questions || [])
            .filter((question) => this.is_document_question(question))
            .map((question) => this.build_document_record(question, section, sub_section))
        )
      );
    },
    flatten_assigned_document_questions(assigned_tree) {
      return (Array.isArray(assigned_tree) ? assigned_tree : []).flatMap((form) =>
        (form?.main_sections || []).flatMap((main_section) =>
          (main_section?.sub_sections || []).flatMap((sub_section) =>
            (sub_section?.questions || [])
              .filter((question) => this.is_document_question(question))
              .map((question) => ({
                id: question.id || question.section_question_id,
                section_question_id: Number(question.id || question.section_question_id || 0),
                name: question.question_text || 'Document field',
                type: this.get_question_type(question) || 'File',
                status: question.is_mandatory ? 'Required' : 'Optional',
                form_name: form.form_name || '',
                section_name: sub_section.sub_section_name || main_section.main_section_name || '',
                requires_verification: Boolean(question.requires_verification),
                compliance_field_id: question.compliance_field_id || null
              }))
          )
        )
      );
    },
    async fetch_forms() {
      this.loading = true;
      this.loading_forms = true;

      try {
        const response = await axios.get(GET_FORMS_URL, get_request_config());
        this.forms = this.extract_response_items(response.data)
          .map((form) => this.normalize_form(form))
          .filter((form) => form.id);
        this.documents = [];
      } catch (error) {
        const response_data = this.parse_response_data(error?.response?.data || {});
        this.forms = [];
        this.documents = [];
        this.show_error(response_data.message || 'Failed to load forms.');
      } finally {
        this.loading = false;
        this.loading_forms = false;
      }
    },
    async fetch_rules_for_selected_form() {
      const form_id = Number(this.selected_form_id);
      if (!Number.isFinite(form_id) || form_id <= 0) {
        this.rules = [];
        this.selected_rule_id = '';
        return;
      }

      this.loading_rules = true;

      try {
        this.rules = await this.fetch_rules_matching_selected_form_from_assignments(form_id);

        if (!this.rules.some((rule) => String(rule.id) === String(this.selected_rule_id))) {
          this.selected_rule_id = '';
          this.assigned_rule_documents = [];
          this.visible_rule_document_ids = [];
        }
      } catch {
        this.rules = [];
        this.selected_rule_id = '';
        this.assigned_rule_documents = [];
        this.visible_rule_document_ids = [];
      } finally {
        this.loading_rules = false;
      }
    },
    is_assigned_form_match(assigned_form, form_id) {
      const assigned_form_id = Number(assigned_form?.form_id || assigned_form?.id || 0);
      if (assigned_form_id && assigned_form_id === Number(form_id)) {
        return true;
      }

      const selected_form_name = String(this.selected_form?.form_name || '').trim().toLowerCase();
      const assigned_form_name = String(assigned_form?.form_name || assigned_form?.name || '').trim().toLowerCase();
      return Boolean(selected_form_name && assigned_form_name && selected_form_name === assigned_form_name);
    },
    async fetch_rules_matching_selected_form_from_assignments(form_id) {
      const response = await axios.get(GET_COMPLIANCE_RULES_URL, get_request_config());
      const all_rules = this.extract_rules_response_items(response.data)
        .map((rule) => this.normalize_rule(rule))
        .filter((rule) => rule.id);

      const rule_checks = await Promise.all(
        all_rules.map((rule) => this.fetch_rule_if_assigned_to_form(rule, form_id))
      );

      return rule_checks.filter(Boolean);
    },
    async fetch_rule_if_assigned_to_form(rule, form_id) {
      const assignment_response = await axios.get(
        GET_ASSIGNED_QUESTIONS_FOR_RULE_URL,
        get_request_config({
          params: {
            compliance_rule_id: rule.id
          }
        })
      ).catch(() => null);

      if (!assignment_response) return null;

      const assigned_forms = this.extract_response_items(assignment_response.data);
      const is_assigned_to_form = assigned_forms.some((form) =>
        this.is_assigned_form_match(form, form_id)
      );

      return is_assigned_to_form ? rule : null;
    },
    async fetch_transition_rules_for_selected_form() {
      const form_id = Number(this.selected_form_id);
      if (!Number.isFinite(form_id) || form_id <= 0) {
        this.transition_rules = [];
        this.selected_transition_rule_id = '';
        return;
      }

      this.loading_transition_rules = true;

      try {
        const response = await axios.get(
          LIST_FORM_TRANSITION_RULES_URL,
          get_request_config({ params: { form_id } })
        );
        const transition_rules = this.extract_response_items(response.data)
          .map((rule) => this.normalize_transition_rule(rule))
          .filter((rule) => rule.id);

        this.transition_rules = transition_rules;
        if (!transition_rules.some((rule) => String(rule.id) === String(this.selected_transition_rule_id))) {
          this.selected_transition_rule_id = '';
        }
      } catch {
        this.transition_rules = [];
        this.selected_transition_rule_id = '';
        this.show_error('Failed to load transition rules for the selected form.');
      } finally {
        this.loading_transition_rules = false;
      }
    },
    async fetch_documents_for_selected_rule(sync_visible_documents = true) {
      if (!this.selected_rule_id) {
        this.assigned_rule_documents = [];
        this.visible_rule_document_ids = [];
        return;
      }

      this.loading_rule_documents = true;

      try {
        const response = await axios.get(
          GET_ASSIGNED_QUESTIONS_FOR_RULE_URL,
          get_request_config({
            params: {
              compliance_rule_id: this.selected_rule_id
            }
          })
        );
        const assigned_tree = this.extract_response_items(response.data);
        this.assigned_rule_documents = this.flatten_assigned_document_questions(assigned_tree);
        this.sync_selected_documents_with_rule(sync_visible_documents);
      } catch {
        this.assigned_rule_documents = [];
      } finally {
        this.loading_rule_documents = false;
      }
    },
    async fetch_documents_for_all_rules() {
      if (!this.rules.length) {
        this.all_rule_documents = [];
        return;
      }

      const results = await Promise.all(
        this.rules.map((rule) => this.fetch_documents_for_rule_assignment(rule))
      );

      this.all_rule_documents = results.flat();
    },
    async fetch_documents_for_rule_assignment(rule) {
      const response = await axios.get(
        GET_ASSIGNED_QUESTIONS_FOR_RULE_URL,
        get_request_config({
          params: {
            compliance_rule_id: rule.id
          }
        })
      ).catch(() => null);

      if (!response) return [];

      const assigned_tree = this.extract_response_items(response.data);
      return this.flatten_assigned_document_questions(assigned_tree).map((document) => ({
        ...document,
        rule_id: Number(rule.id),
        rule_name: rule.name
      }));
    },
    sync_selected_documents_with_rule(sync_visible_documents = true) {
      if (!this.selected_rule_id || !this.documents.length) {
        return;
      }

      const assigned_ids = this.assigned_rule_section_question_ids;
      const current_form_assigned_ids = this.documents
        .filter((document) => assigned_ids.has(Number(document.section_question_id)))
        .map((document) => String(document.section_question_id));

      this.selected_document_ids = current_form_assigned_ids;
      if (sync_visible_documents) {
        this.visible_rule_document_ids = current_form_assigned_ids;
      }
    },
    async fetch_documents_for_selected_form() {
      const form_id = Number(this.selected_form_id);
      if (!Number.isFinite(form_id) || form_id <= 0) {
        this.documents = [];
        return;
      }

      this.loading = true;
      this.loading_documents = true;

      try {
        const response = await axios.get(
          GET_FORM_STRUCTURE_URL,
          get_request_config({ params: { form_id } })
        );
        await this.fetch_rules_for_selected_form();
        const form_structure = this.extract_response_items(response.data);
        this.documents = this.extract_document_questions(form_structure);
        await this.fetch_transition_rules_for_selected_form();
        await this.fetch_documents_for_all_rules();
        this.selected_document_ids = this.documents
          .filter((document) => this.is_document_assigned_to_any_rule(document))
          .map((document) => String(document.section_question_id));
        await this.fetch_documents_for_selected_rule();
      } catch (error) {
        const response_data = this.parse_response_data(error?.response?.data || {});
        this.documents = [];
        this.show_error(response_data.message || 'Failed to load documents for the selected form.');
      } finally {
        this.loading = false;
        this.loading_documents = false;
      }
    },
    async toggle_document_selection(document) {
      if (!this.selected_rule_id) {
        return;
      }

      const section_question_id = String(document?.section_question_id || '');
      if (!section_question_id) {
        return;
      }

      const is_selected = this.is_document_assigned_to_selected_rule(document);
      const next_selected_ids = is_selected
        ? this.selected_document_ids.filter((id) => id !== section_question_id)
        : [...this.selected_document_ids, section_question_id];

      this.selected_document_ids = next_selected_ids;
      this.updating_document_rule_id = section_question_id;

      try {
        await this.persist_document_rule_assignment(Number(section_question_id), !is_selected);
        await this.fetch_documents_for_selected_rule(false);
        await this.fetch_documents_for_all_rules();
        this.show_success(
          is_selected
            ? 'Document unlinked from compliance rule.'
            : 'Document linked to compliance rule.'
        );
      } catch (error) {
        this.selected_document_ids = is_selected
          ? [...this.selected_document_ids, section_question_id]
          : this.selected_document_ids.filter((id) => id !== section_question_id);

        const response_data = this.parse_response_data(error?.response?.data || {});
        this.show_error(response_data.message || 'Failed to update the selected rule document.');
      } finally {
        this.updating_document_rule_id = '';
      }
    },
    persist_document_rule_assignment(section_question_id, should_assign) {
      const url = should_assign
        ? BULK_ASSIGN_COMPLIANCE_QUESTIONS_URL
        : BULK_UNASSIGN_COMPLIANCE_QUESTIONS_URL;

      return axios.post(
        url,
        {
          compliance_rule_id: Number(this.selected_rule_id),
          section_question_ids: [section_question_id]
        },
        get_request_config()
      );
    },
    is_document_assigned_to_selected_rule(document) {
      const section_question_id = Number(document?.section_question_id || 0);
      const rule_id = Number(this.selected_rule_id || 0);

      if (!section_question_id || !rule_id) {
        return false;
      }

      return this.all_rule_documents.some((assigned_document) =>
        Number(assigned_document.section_question_id) === section_question_id &&
        Number(assigned_document.rule_id) === rule_id
      );
    },
    is_document_assigned_to_any_rule(document) {
      return this.all_rule_section_question_ids.has(Number(document?.section_question_id));
    },
    is_document_visible_for_selected_rule(document) {
      const section_question_id = String(document?.section_question_id || '');
      return Boolean(section_question_id) && this.visible_rule_document_ids.includes(section_question_id);
    },
  }
};
